import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BookingRules, mergeBookingRules } from '../shared/bookingRules';

/**
 * PER-SHOP BOOKING RULES — the ONE customer-readable source of the shop's booking rules
 * (2026-08-07).
 *
 * SLIK is multi-tenant: every barbershop tunes its own booking behavior — back-to-back vs a
 * fixed grid, buffers, slot interval, the no-show policy — from POS Settings. The owner saves
 * to settings/shop (STAFF-ONLY), which mirrors the rules to publicConfig/bookingRules
 * (PUBLIC-READ) via shopSettingsService.mirrorPublicBookingRules. Every customer-facing surface
 * (customer Book Now, the public guest app) reads the rules through THIS service so availability
 * is computed the SAME way the owner configured. Mirrors publicHoursService exactly.
 *
 * BEHAVIOR-PIN: shared/bookingRules.ts / project-slik-pos-booking-rules-config-2026-08-07
 */
class PublicBookingRulesService {
  private cache: BookingRules | null = null;
  private cacheAt = 0;
  private readonly TTL_MS = 60_000; // 1 min — rules change rarely; keep surfaces fresh-ish

  /** Read the public booking-rules mirror (publicConfig/bookingRules). Cached briefly. */
  async getRules(): Promise<BookingRules> {
    const now = Date.now();
    if (this.cache && now - this.cacheAt < this.TTL_MS) {
      return this.cache;
    }
    try {
      const snap = await getDoc(doc(db, 'publicConfig', 'bookingRules'));
      if (snap.exists() && snap.data()?.bookingRules) {
        // mergeBookingRules fills any missing field with the safe default.
        this.cache = mergeBookingRules(snap.data()!.bookingRules);
        this.cacheAt = now;
        return this.cache;
      }
      console.warn('⚠️ publicConfig/bookingRules missing — using default booking rules');
    } catch (err) {
      console.warn('⚠️ Could not read public booking rules, using defaults:', err);
    }
    this.cache = mergeBookingRules(null); // full defaults
    this.cacheAt = now;
    return this.cache;
  }

  /** Force a re-read on next call (e.g. after the owner saves). */
  invalidate(): void {
    this.cache = null;
    this.cacheAt = 0;
  }
}

export const publicBookingRulesService = new PublicBookingRulesService();
